import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icons } from 'components/Icons';
import { 
  getSortedBookmarks, 
  removeFromBookmarks 
} from 'utils/bookmarks';

/**
 * Bookmarks Section Component
 * Displays user's bookmarked pages in the navigation sidebar
 */
const BookmarksSection = ({ onCloseMenu }) => {
  const [bookmarks, setBookmarks] = useState([]);
  const location = useLocation();

  // Load bookmarks on component mount and when location changes
  useEffect(() => {
    const loadBookmarks = () => {
      const sortedBookmarks = getSortedBookmarks();
      setBookmarks(sortedBookmarks);
    };
    
    loadBookmarks();
    
    // Listen for storage changes (from other tabs and same tab)
    const handleStorageChange = (e) => {
      if (e.key === 'besper_bookmarks') {
        loadBookmarks();
      }
    };
    
    // Listen for custom bookmark change events
    const handleBookmarkChange = () => {
      loadBookmarks();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('bookmarkChanged', handleBookmarkChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('bookmarkChanged', handleBookmarkChange);
    };
  }, []);

  // Also reload bookmarks when location changes
  useEffect(() => {
    const sortedBookmarks = getSortedBookmarks();
    setBookmarks(sortedBookmarks);
  }, [location.pathname]);

  // Get language-aware path helper
  const getLocalizedPath = (path) => {
    const currentPath = location.pathname;
    const currentLangMatch = currentPath.match(/^\/([a-z]{2})\//);
    
    if (currentLangMatch) {
      const lang = currentLangMatch[1];
      // Remove any existing language prefix from the path
      const cleanPath = path.replace(/^\/[a-z]{2}(?=\/)/, '') || '/';
      return `/${lang}${cleanPath}`;
    }
    return path;
  };

  // Check if current route is active
  const isActiveRoute = (path) => {
    const cleanCurrentPath = location.pathname.replace(/^\/[a-z]{2}(?=\/)/, '') || '/';
    const cleanBookmarkPath = path.replace(/^\/[a-z]{2}(?=\/)/, '') || '/';
    return cleanCurrentPath === cleanBookmarkPath;
  };

  // Handle removing a bookmark
  const handleRemoveBookmark = (e, bookmarkPath) => {
    e.preventDefault();
    e.stopPropagation();
    
    const success = removeFromBookmarks(bookmarkPath);
    if (success) {
      setBookmarks(prev => prev.filter(bookmark => bookmark.path !== bookmarkPath));
      
      // Show brief feedback
      const button = e.target.closest('.bookmark-remove-btn');
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '✓';
        button.style.color = '#28a745';
        setTimeout(() => {
          button.innerHTML = originalText;
          button.style.color = '';
        }, 1000);
      }
    }
  };

  // Don't render if no bookmarks exist
  if (bookmarks.length === 0) {
    return null;
  }

  return (
    <div className="bookmarks-section">
      {/* Bookmarks Header */}
      <div className="bookmarks-header">
        <div className="bookmarks-title">
          <Icons.Bookmark size={16} />
          <span>Bookmarks</span>
        </div>
      </div>

      {/* Bookmarks List */}
      {bookmarks.length > 0 && (
        <ul className="bookmarks-list">
          {bookmarks.map((bookmark) => (
            <li key={bookmark.id} className="bookmark-item">
              <Link
                to={getLocalizedPath(bookmark.path)}
                className={`bookmark-link ${isActiveRoute(bookmark.path) ? 'active' : ''}`}
                onClick={onCloseMenu}
              >
                <span className="bookmark-label">{bookmark.label}</span>
                <button
                  className="bookmark-remove-btn"
                  onClick={(e) => handleRemoveBookmark(e, bookmark.path)}
                  title="Remove from bookmarks"
                  aria-label={`Remove ${bookmark.label} from bookmarks`}
                >
                  <Icons.X size={12} />
                </button>
              </Link>
            </li>
          ))}
        </ul>
      )}


      {/* Separator */}
      <div className="bookmarks-separator"></div>
    </div>
  );
};

export default BookmarksSection;